import {NgClass, NgStyle} from '@angular/common';
import {Component, computed, input, InputSignal, model, ModelSignal, Signal} from '@angular/core';

import {AnimationProp, FaIconInit, FaIconProps, FaStackIconProps, FlipProp, isFaDuotoneIconProps, PullProp, RotateProp, SizeProp} from './data/fa-icon.types';

/**
 * Font	Awesome Icon Component:
 * Options in font awesome class:
 *
 *
 */

@Component({
	selector: 'fa-icon',
	imports: [NgClass, NgStyle],
	templateUrl: './fa-icon.component.html',
	styleUrls: ['./fa-icon.component.scss'],
})
export class FaIconComponent {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */

	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	readonly init: InputSignal<FaIconInit> = input<FaIconInit>('icon');

	readonly prefix: ModelSignal<FaIconProps['prefix']> = model<FaIconProps['prefix']>('fas');
	readonly faStyle: ModelSignal<FaIconProps['faStyle']> = model<FaIconProps['faStyle']>('fa-solid');
	readonly iconName: ModelSignal<FaIconProps['iconName']> = model.required<FaIconProps['iconName']>();
	readonly color: ModelSignal<FaIconProps['color']> = model<FaIconProps['color']>();
	readonly animation: ModelSignal<AnimationProp> = model<AnimationProp>();
	readonly border: ModelSignal<FaIconProps['border']> = model<FaIconProps['border']>();
	readonly fixedWidth: ModelSignal<boolean> = model<boolean>();
	readonly counter: ModelSignal<FaIconProps['counter']> = model<FaIconProps['counter']>(NaN);
	readonly inverse: ModelSignal<boolean> = model<boolean>();
	readonly flip: ModelSignal<FlipProp> = model<FlipProp>();
	readonly size: ModelSignal<SizeProp> = model<SizeProp>();
	readonly pull: ModelSignal<PullProp> = model<PullProp>();
	readonly rotate: ModelSignal<RotateProp> = model<RotateProp>();

	readonly outerClass: ModelSignal<string> = model<string>();

	/* ----------------------------------------------- STACK ICON ----------------------------------------------- */
	readonly backgroundIcon: ModelSignal<FaStackIconProps['backgroundIcon']> = model<FaStackIconProps['backgroundIcon']>();
	readonly foregroundIcon: ModelSignal<FaStackIconProps['foregroundIcon']> = model<FaStackIconProps['foregroundIcon']>();

	/* ---------------- Duotone Only ---------------- */
	readonly primaryColor: ModelSignal<string> = model<string>();
	readonly secondaryColor: ModelSignal<string> = model<string>();

	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	// Stack
	protected readonly bgIconData$ = computed(() => {
		if (!this.backgroundIcon()) return;
		const faIconProps: FaIconProps = {
			prefix: this.backgroundIcon().prefix,
			iconName: this.backgroundIcon().iconName,
			size: this.backgroundIcon().stackItemSize,
		};
		return this.#generateIcon(faIconProps);
	});
	protected readonly fgIconData$ = computed(() => {
		if (!this.foregroundIcon()) return;
		const faIconProps: FaIconProps = {
			prefix: this.foregroundIcon().prefix,
			iconName: this.foregroundIcon().iconName,
			size: this.foregroundIcon().stackItemSize,
		};
		return this.#generateIcon(faIconProps);
	});
	/* ------------------------------------------------  Signals ------------------------------------------------ */
	#iconData$ = computed(() => {
		if (!this.iconName()) return;
		const prefix = this.prefix();
		const faStyle = prefix ? '' : this.faStyle();

		const baseProps = {
			iconName: this.iconName(),
			color: this.color(),
			animation: this.animation(),
			border: this.border(),
			fixedWidth: this.fixedWidth(),
			counter: this.counter(),
			inverse: this.inverse(),
			flip: this.flip(),
			size: this.size(),
			pull: this.pull(),
			rotate: this.rotate(),
		};

		let faIconProps: FaIconProps;
		if (prefix) {
			faIconProps = {...baseProps, prefix};
		} else if (faStyle) {
			faIconProps = {...baseProps, faStyle};
		} else {
			faIconProps = {...baseProps, prefix: 'fas'};
		}

		if (faIconProps.prefix === 'fad' || faIconProps.faStyle === 'duotone') {
			faIconProps = {
				...faIconProps,
				prefix: 'fad',
				primaryColor: this.primaryColor() || '',
				secondaryColor: this.secondaryColor() || '',
			};
		}

		return this.#generateIcon(faIconProps);
	});
	protected readonly iconClass$: Signal<string> = computed(() => this.#iconData$()?.iconClass);
	protected readonly iconStyles$: Signal<Record<string, string>> = computed(() => this.#iconData$()?.iconStyles);

	/* -------------------------------------------------- Data -------------------------------------------------- */

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	#generateIcon(faIconProps: FaIconProps): {
		iconClass: string;
		iconStyles: Record<string, string>;
		iconText: string;
	} {
		let iconClass = `${faIconProps.prefix} fa-${faIconProps.iconName}`;
		const iconStyles: Record<string, string> = {};
		let iconText = '';

		// Font Awesome Style
		const faStyleClass = (faIconProps.faStyle && (faIconProps.faStyle?.startsWith('fa-') ? faIconProps.faStyle : `fa-${faIconProps.faStyle}`)) || '';
		iconClass += ` ${faStyleClass}`;
		// Color
		if (faIconProps.color) faIconProps.color.includes('-') ? (iconClass += ` text-${faIconProps.color}`) : (iconStyles['--fa-primary-color'] = faIconProps.color);
		if (isFaDuotoneIconProps(faIconProps)) {
			if (faIconProps.primaryColor) iconStyles['--fa-primary-color'] = faIconProps.primaryColor;
			if (faIconProps.secondaryColor) iconStyles['--fa-secondary-color'] = faIconProps.secondaryColor;
		}

		//	Animation
		if (faIconProps.animation) iconClass += ` fa-${faIconProps.animation}`;
		// Fixed Width
		if (faIconProps.fixedWidth) iconClass += ` fa-fw`;
		//	Counter
		if (!isNaN(faIconProps.counter)) {
			iconClass += ` fa-layers fa-fw`;
			iconText = faIconProps.counter.toString();
		}
		// Inverse (invert color of the icon -	help when the icon is on a dark background)
		if (faIconProps.inverse) iconClass += ` fa-inverse`;
		// Flip
		if (faIconProps.flip) {
			iconClass += ` fa-flip`;
			switch (faIconProps.flip) {
				case 'x':
					iconStyles['--fa-flip-x'] = '1';
					break;
				case 'y':
					iconStyles['--fa-flip-y'] = '1';
					break;
				case 'xy':
					iconStyles['--fa-flip-x'] = '1';
					iconStyles['--fa-flip-y'] = '1';
					break;
			}
		}
		// Icon Size
		if (faIconProps.size) iconClass += ` fa-${faIconProps.size}`;
		// Pull
		if (faIconProps.pull) iconClass += ` fa-pull-${faIconProps.pull}`;
		// Border
		if (faIconProps.border) {
			const borderProps = faIconProps.border;
			for (const prop in borderProps) {
				if (borderProps[prop]) iconStyles[`--fa-border-${prop}`] = borderProps[prop];
			}
		}
		// Rotate
		if (faIconProps.rotate) iconClass += ` fa-rotate-${faIconProps.rotate}`;

		return {iconClass, iconStyles, iconText};
	}
}
